import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { Dayjs } from "dayjs";
import { DateValidationError } from "@mui/x-date-pickers/models";
import { useTheme } from "@mui/material";
import clsx from "clsx";

import Calendar from "icons/Calendar";

import {
  StyledDatePicker,
  StyledDatePickerContainer,
  StyledDatePickerError,
  StyledDatePickerLabel,
  StyledDatePickerPopper,
} from "styles/components/date-picker";

type ErrorState = {
  error: boolean;
  description?: DateValidationError | null;
};

interface DatePickerProps {
  value?: Dayjs | null;
  defaultValue?: Dayjs | null;
  className?: string;
  label?: string;
  dataTestId?: string;
  required?: boolean;
  error?: boolean;
  placeholder?: string;
  disabled?: boolean;
  withErrorField?: boolean;
  errorText?: string;
  minDate?: any;
  maxDate?: any;
  disableFuture?: boolean;
  disablePast?: boolean;
  clearable?: boolean;

  onChange?: (value: any) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const DatePicker: FC<DatePickerProps> = ({
  value,
  defaultValue,
  className,
  label = "Label",
  dataTestId,
  required,
  error,
  disabled,
  withErrorField = true,
  errorText = "Error text",
  minDate,
  maxDate,
  disableFuture,
  disablePast,
  clearable = true,
  placeholder,
  onChange,
  onBlur,
  onFocus,
}) => {
  const theme = useTheme();

  const [errorState, setErrorState] = useState<ErrorState>({
    error: error ?? false,
    description: null,
  });

  const [active, setActive] = useState<boolean>(false);

  useEffect(() => {
    if (error) {
      setErrorState((prev) => ({ ...prev, error }));
    }
  }, [error]);

  const wrapClassName = clsx(["date-picker-container", className]);

  const errorClassName = clsx("date-picker-error", {
    "date-picker-error_visible": errorState.error,
  });

  const renderedLabel = useMemo(
    () =>
      label ? (
        <StyledDatePickerLabel
          data-testid={dataTestId && `${dataTestId}-label`}
        >
          {label}
          {required ? <span className="date-picker__marker">*</span> : null}
        </StyledDatePickerLabel>
      ) : null,
    [dataTestId, label, required]
  );

  const renderedErrorText = useMemo(() => {
    if (errorState.description && errorState.description.length > 0) {
      switch (errorState.description) {
        case "minDate":
          return `You cannot select a date before ${minDate}`;

        case "maxDate":
          return `You cannot select a date after ${maxDate}`;

        case "disableFuture":
          return "You cannot select a date from the future";

        case "disablePast":
          return "You cannot select a date from the past";

        case "invalidDate":
          return "Please enter the correct date";
        default:
          return "";
      }
    }

    return errorText;
  }, [errorState.description, errorText, maxDate, minDate]);

  const renderedErrorField = useMemo(
    () =>
      withErrorField ? (
        <StyledDatePickerError
          variant="bodyM"
          fontWeight={300}
          className={errorClassName}
          data-testid={dataTestId && `${dataTestId}-error`}
        >
          {renderedErrorText}
        </StyledDatePickerError>
      ) : null,
    [dataTestId, errorClassName, renderedErrorText, withErrorField]
  );

  const textFieldProps = useMemo(
    () =>
      placeholder
        ? {
            placeholder,
            error: errorState.error,
            onFocus,
            onBlur,
          }
        : {
            error: errorState.error,
            onFocus,
            onBlur,
          },
    [errorState.error, onBlur, onFocus, placeholder]
  );

  const onError = useCallback((error: DateValidationError) => {
    if (error) {
      setErrorState({ error: true, description: error });
    } else {
      setErrorState({ error: false, description: null });
    }
  }, []);

  const handlePickerChange = useCallback(
    (value: any) => onChange?.(value),
    [onChange]
  );

  const handlePickerOpen = useCallback(() => setActive(true), []);

  const handlePickerClose = useCallback(() => setActive(false), []);

  return (
    <StyledDatePickerContainer
      className={wrapClassName}
      data-testid={dataTestId}
    >
      {renderedLabel}
      <StyledDatePicker
        value={value}
        defaultValue={defaultValue}
        label={null}
        disableFuture={disableFuture}
        disablePast={disablePast}
        maxDate={maxDate}
        minDate={minDate}
        views={["month", "year", "day"]}
        slotProps={{
          textField: textFieldProps,
          field: {
            clearable,
          } as any,
        }}
        slots={{
          openPickerIcon: () => (
            <Calendar color={active ? theme.palette.primary.main : undefined} />
          ),
          popper: StyledDatePickerPopper,
        }}
        disabled={disabled}
        onError={onError}
        onChange={handlePickerChange}
        onClose={handlePickerClose}
        onOpen={handlePickerOpen}
        dayOfWeekFormatter={
          ((_day: any, weekday: any) => `${weekday.format("dd")}`) as any
        }
        data-testid={dataTestId && `${dataTestId}-picker`}
      />
      {renderedErrorField}
    </StyledDatePickerContainer>
  );
};

export default DatePicker;
